RivalIQ.apps.Favorites.module('curations', function (curations, App, Backbone, Marionette, $, _) {

    'use strict';

    const GLOBAL_DASHBOARD_GROUP_ID = '__global__';

    function accessibleTemplates(templates) {
        return _.filter(templates, function (template) {
            return !template.requiredBeta || RivalIQ.state.account.inBeta(template.requiredBeta);
        });
    }

    const Create = Marionette.Dashboard.views.ItemView.mixin({

        mixins: {
            Props: Marionette.Dashboard.mixins.Props
        },

        behaviors: {
            FormValidator: {
                bindModel: false
            },
            AttributeBuilder: {},
            Information: {},
            Localizable: {}
        },

        ui: {
            deleteBtn: '.x-delete',
            saveBtn: '.x-save',
            clone: '[name="parent_widget_curation_configuration_id"]',
            name: '[name="name"]',
            copyMode: '[name="copy"]',
            templateBtn: '.x-template-btn',
            copyFrom: '.x-copy-from',
            visibilityToggle: '[name="type"]',
            postTagIdsWarning: '.x-post-tag-ids-warning',
            companySetsWarning: '.x-company-sets-warning'
        },

        eventsNotToDebounce: {
            'keypress @ui.name': 'onKeypress',
            'change @ui.copyMode': 'onSetupChange',
            'click @ui.deleteBtn': 'onDelete',
            'click @ui.saveBtn': 'onSave',
            'change @ui.visibilityToggle': 'onVisibilityChange'
        },

        getTemplate: function () {
            const me = this;

            let template = '#favoritesCreateTemplate';

            const copyMode = me.getProp('copyMode');
            if (copyMode === App.enums.copyMode.DASHBOARD) {
                template = '#favoritesCreateFromExisting';
            } else if (copyMode === App.enums.copyMode.NONE) {
                template = '#favoritesCreateFromBlank';
            }

            return template;
        },

        onRenderLoaded: function () {
            const me = this;

            if (me.model.isNew()) {
                me.ui.templateBtn.toggleClass('disabled', !me.allDashboards.length);
            }

            me.onSetupChange();

            const copyMode = me.getProp('copyMode');
            if (copyMode === App.enums.copyMode.DASHBOARD) {
                me.requestRenderedFrame(function () {
                    _.head(me.ui.clone).selectize.focus();
                });
            }
        },

        buildDropdown: function (options) {
            const me = this;

            const config = _.extend({
                selectOnTab: true,
                labelField: 'name',
                searchField: 'name',
                valueField: 'id',
                noEmptySelection: true,
                items: [],
                wrapperClass: 'riq-select',
                inputClass: 'riq-select-input',
                dropdownClass: 'riq-select-dropdown',
                dropdownContentClass: 'riq-select-dropdown-content'
            }, options);

            const selectize = _.get(me.ui.clone, [0, 'selectize']);
            if (selectize) {
                selectize.destroy();
            }

            me.ui.clone.selectize(config);
        },

        buildTemplateDropdown: function () {
            const me = this;

            const templates = _.filter(me.templates, function (template) {
                const source = _.get(template, ['props', 'source']);
                return RivalIQ.state.account.channelDisplay(source);
            });

            me.buildDropdown({
                options: templates,
                onChange: function (id) {
                    const selector = this;
                    me.ui.name.val(selector.options[id].name);
                }
            });
        },

        buildCloneDropdown: function () {
            const me = this;

            me.buildDropdown({
                options: me.allDashboards,
                optgroups: me.dashboardGroups,
                optgroupValueField: 'group_id',
                optgroupField: 'group_id',
                lockOptgroupOrder: true,
                render: {
                    option: function (item) {
                        let iconClassName = 'fa-sitemap';
                        if (item.type === RivalIQ.enums.visibility.LANDSCAPE) {
                            iconClassName = 'fa-th-large';
                        }

                        return $.tagHtml('div', {
                            html: [
                                $.tagHtml('i', {
                                    class: ['fa', 'fa-fw', iconClassName]
                                }),
                                item.name
                            ].join('&nbsp;')
                        });
                    }
                },
                onChange: function (id) {
                    const selector = this;
                    me.ui.name.val(`${selector.options[id].name} Copy`);
                }
            });
        },

        loadData: function () {
            const me = this;

            if (!me.model.isNew()) {
                const deferred = new $.Deferred();
                deferred.resolve();
                return deferred;
            }

            const accountId = me.getProp('account_id');
            const curationId = RivalIQ.enums.curation.DASHBOARDS;

            function buildGroups(dashboards) {
                const groups = _.reduce(dashboards, function (memo, dashboard) {
                    let label = dashboard.owner_name;
                    if (dashboard.landscape_id) {
                        label += ' Landscape';
                    }

                    return _.set(memo, dashboard.landscape_id, {
                        group_id: dashboard.landscape_id || GLOBAL_DASHBOARD_GROUP_ID,
                        label: label
                    });
                }, {});

                const values = _.values(groups);
                // NOTE we have to sort to make sure we sort global first, then
                // alphabetical (by group name)
                values.sort(function (a, b) {
                    if (a.group_id === GLOBAL_DASHBOARD_GROUP_ID) {
                        return -1;
                    } else if (b.group_id === GLOBAL_DASHBOARD_GROUP_ID) {
                        return 1;
                    } else {
                        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
                    }
                });

                return values;
            }

            function buildItems(dashboards) {
                const items = _.map(dashboards, function (dashboard) {
                    return _.defaults({
                        name: _.unescape(dashboard.name),
                        group_id: dashboard.landscape_id || GLOBAL_DASHBOARD_GROUP_ID
                    }, dashboard);
                });

                return items;
            }

            const getDashboards = $.getJSON([
                '/api/accounts',
                accountId,
                'configurations',
                curationId
            ].join('/'));

            return $.when(getDashboards, $.getJSON('/api/configurations/dashboards'))
                .then(function (dashboardArgs, templatesArgs) {
                    const dashboards = _.head(dashboardArgs);
                    me.dashboardGroups = buildGroups(dashboards);
                    me.allDashboards = buildItems(dashboards);
                    me.templates = accessibleTemplates(_.head(templatesArgs));
                });
        },

        onDelete: function () {
            const me = this;
            App.utils.showDashboardDeleteModal(me);
        },

        onKeypress: function (e) {
            const me = this;
            if (e.which === 13) {
                e.preventDefault();
                me.onSave();
            }
        },

        onSetupChange: function () {
            const me = this;
            const copy = (me.formAttrs.copy || me.getProp('copyMode'));

            if (copy === App.enums.copyMode.DASHBOARD) {
                me.buildCloneDropdown();
            } else if (copy === App.enums.copyMode.TEMPLATE) {
                me.buildTemplateDropdown();
            }

            me.ui.copyFrom.toggleClass('display-none', !copy);
        },

        onVisibilityChange: function (e) {
            const me = this;

            let showPostTagIdsWarning = false;
            let showCompanySetsWarning = false;
            const $target = $(e.target);
            const newVisibility = Number($target.val());
            if (newVisibility === RivalIQ.enums.visibility.ACCOUNT) {
                // We only want to show the post tags warning if this dashboard has a widget definition with a non-empty postTagIds prop set.
                showPostTagIdsWarning = _.some(me.model.get('definitions'), function (definition) {
                    const postTagIdsProp = _.get(definition, ['props', 'postTagIds']);
                    return !_.isEmpty(postTagIdsProp);
                });

                // We only want to show the post tags warning if this dashboard has a widget definition with a non-empty onlyCompanySetIds prop set.
                showCompanySetsWarning = _.some(me.model.get('definitions'), function (definition) {
                    const onlyCompanySetIdsProp = _.get(definition, ['props', 'onlyCompanySetIds']);
                    return !_.isEmpty(onlyCompanySetIdsProp);
                });
            }

            if (showPostTagIdsWarning) {
                me.ui.postTagIdsWarning.show();
            } else {
                me.ui.postTagIdsWarning.hide();
            }

            if (showCompanySetsWarning) {
                me.ui.companySetsWarning.show();
            } else {
                me.ui.companySetsWarning.hide();
            }
        },

        onSave: function () {
            const me = this;

            let attrs = me.formAttrs;
            const copy = attrs.copy;
            if (attrs.copy) {
                attrs = _.omit(attrs, 'copy');
                if (copy === App.enums.copyMode.TEMPLATE) {
                    const template = _.find(me.templates, {
                        id: me.ui.clone.val()
                    });
                    _.extend(attrs, _.pick(template, 'definitions', 'props'));
                }
            } else {
                attrs = _.omit(attrs, 'copy', 'parent_widget_curation_configuration_id');
            }

            const deferred = me.model.save(attrs, {
                copy: me.formAttrs.copy
            });

            if (!deferred) {
                return;
            }

            me.ui.saveBtn.button('loading');
            deferred
                .done(function () {
                    const urlPath = ['/dashboards', me.model.id];
                    if (me.getProp('copyMode') === App.enums.copyMode.NONE) {
                        urlPath.push('create');
                    }
                    const url = urlPath.join('/');
                    RivalIQ.execute('history:navigate', url, {
                        trigger: true
                    });
                })
                .fail(function () {
                    RivalIQ.execute('alert:danger', 'An error occurred, please try again');
                    me.ui.saveBtn.button('reset');
                });
        },

        templateHelpers: function () {
            const me = this;

            return {
                copyMode: App.enums.copyMode,
                isNew: me.model.isNew(),
                visibilities: [
                    {
                        id: RivalIQ.enums.visibility.LANDSCAPE,
                        icon: 'fa-th-large',
                        name: 'Landscape'
                    },
                    {
                        id: RivalIQ.enums.visibility.ACCOUNT,
                        icon: 'fa-sitemap',
                        name: 'Account'
                    }
                ]
            };
        },

        constructor: function (attributes) {
            const me = this;

            me.model = attributes.dashboardModel.configuration;
            Marionette.Dashboard.views.ItemView.apply(me, arguments);
        }
    });


    curations.Create = RivalIQ.apps.Dashboard.models.Curation.extend({
        afterFilters: [

            /*
             * Update global reference when configuration changes
             */
            function (curation) {
                const configuration = curation.configuration;

                if (configuration.isNew()) {
                    configuration.listenToOnce(configuration, 'sync', function () {
                        RivalIQ.state.dashboards.add(configuration);
                    });
                } else {
                    configuration.listenToOnce(configuration, 'sync', function () {
                        RivalIQ.state.dashboards.get(configuration.id).set(configuration.attributes);
                    });

                    configuration.listenToOnce(configuration, 'destroy', function () {
                        RivalIQ.state.dashboards.remove(configuration);
                    });
                }
            }
        ],
        title: function () {
            const me = this;
            const name = me.getName();
            return (name ? `Editing ${name}` : 'New Dashboard');
        },

        timePeriod: false,
        companySets: false,

        widgetsView: Create,

        loadConfiguration: function () {
            const me = this;

            if (me.getProp('configurationId')) {
                const configuration = new App.models.Configuration({
                    id: me.getProp('configurationId'),
                    curation_id: RivalIQ.enums.curation.DASHBOARDS,
                    landscape_id: me.getProp('landscapeId'),
                    account_id: me.getProp('account_id')
                });

                const deferred = new $.Deferred();
                configuration.fetch()
                    .fail(function (xhr) {
                        if (xhr.status === 404) {
                            deferred.resolve(App.utils.getDefaultRedirect());
                        } else {
                            deferred.reject(xhr);
                        }
                    })
                    .done(function () {
                        deferred.resolve(configuration);
                    });

                return deferred;
            }

            return $.Deferred().resolve(me.buildConfiguration());
        },

        // NOTE clobber b/c we dont want to parse the widgets returned by the
        // configuration (for now at least)
        getWidgets: function () {
            return new Backbone.Collection();
        },

        configurationModel: App.models.Configuration,

        buildConfiguration: function () {
            const me = this;
            const Model = me.configurationModel;
            const props = me.getProps();

            return new Model({
                name: undefined,
                landscape_id: props.landscapeId,
                account_id: props.account_id,
                curation_id: RivalIQ.enums.curation.DASHBOARDS,
                type: RivalIQ.enums.visibility.LANDSCAPE
            });
        }
    });
});
